import React, { useEffect, useRef } from 'react';
import { useLocale } from '../../contexts/LocaleContext.jsx';
import useMemoryClip from '../../hooks/useMemoryClip.js';
import '../../styles/MemoryClipPlayer.css';

/**
 * The in-page player for one memory's replay clip (ENG-758): the clip of the
 * listener's requested song, cut from SongShake's recording of the show
 * (ENG-757), with its first frame as the poster. Only that song's clip is
 * ever played; the show recording itself is never shown.
 *
 * It sits inline wherever it is placed (the just-played card, a memory's row)
 * and never over the broadcast. A clip still being made, a song with no
 * replay, and a listener without Premium each get a message instead of a
 * player; the Premium message offers `onGoPremium`.
 *
 * An expired link is replaced without the listener noticing: pressing play
 * past `expiresAt` asks for fresh links first, and a video that fails to load
 * asks again once and resumes where it was.
 */
const MemoryClipPlayer = ({ memoryId, title, onGoPremium, autoPlay = true }) => {
  const { t } = useLocale();
  const { state, clip, load, fresh, refresh } = useMemoryClip(memoryId);
  const videoRef = useRef(null);
  const resumeAtRef = useRef(null);
  const retriedUrlRef = useRef(null);

  useEffect(() => {
    load();
  }, [load]);

  const swapIfExpired = async () => {
    const video = videoRef.current;
    if (!video || !clip) return;
    const current = await fresh();
    if (current && current.url !== clip.url) {
      resumeAtRef.current = video.currentTime;
    }
  };

  const recover = async () => {
    const video = videoRef.current;
    // One retry per link: a fresh link that fails as well is left as it is.
    if (!clip || retriedUrlRef.current === clip.url) return;
    retriedUrlRef.current = clip.url;
    resumeAtRef.current = video ? video.currentTime : null;
    await refresh();
  };

  const resume = () => {
    const video = videoRef.current;
    if (!video || resumeAtRef.current == null) return;
    video.currentTime = resumeAtRef.current;
    resumeAtRef.current = null;
    const played = video.play?.();
    if (played && typeof played.catch === 'function') played.catch(() => {});
  };

  let body;
  if (state === 'ready' && clip) {
    body = (
      <video
        ref={videoRef}
        className="memory-clip-video"
        src={clip.url}
        poster={clip.thumbnailUrl || undefined}
        controls
        playsInline
        autoPlay={autoPlay}
        preload="metadata"
        aria-label={title ? t('memoryClip.labelTitled', { title }) : t('memoryClip.label')}
        onPlay={swapIfExpired}
        onError={recover}
        onLoadedMetadata={resume}
        data-testid="memory-clip-video"
      />
    );
  } else if (state === 'processing') {
    body = <p className="memory-clip-note" role="status">{t('memoryClip.processing')}</p>;
  } else if (state === 'unavailable') {
    body = <p className="memory-clip-note" role="status">{t('memoryClip.unavailable')}</p>;
  } else if (state === 'premium') {
    body = (
      <div className="memory-clip-premium" data-testid="memory-clip-premium">
        <p className="memory-clip-note" role="status">{t('memoryClip.premium')}</p>
        {onGoPremium && (
          <button type="button" className="memory-clip-go" onClick={onGoPremium} data-testid="memory-clip-go">
            {t('memoryClip.goPremium')}
          </button>
        )}
      </div>
    );
  } else if (state === 'error') {
    body = (
      <div className="memory-clip-error">
        <p className="memory-clip-note" role="alert">{t('memoryClip.error')}</p>
        <button type="button" className="memory-clip-retry" onClick={load} data-testid="memory-clip-retry">
          {t('common.actions.tryAgain')}
        </button>
      </div>
    );
  } else {
    body = <p className="memory-clip-note" role="status">{t('memoryClip.loading')}</p>;
  }

  return (
    <div className="memory-clip" data-testid="memory-clip" data-state={state}>
      {body}
    </div>
  );
};

export default MemoryClipPlayer;
